import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  useCreateJobScheduleMutation,
  useDeleteJobScheduleMutation,
  useGetJobScheduleQuery,
  useListJobSchedulesQuery,
  useRunJobScheduleNowMutation,
  useUpdateJobScheduleMutation,
} from "~/ui-rtk/api/jobsApi"
import Input from "~/ui-rtk/components/ui/controls/Input"
import Select from "~/ui-rtk/components/ui/controls/Select"
import { BROWSER_TZ, TIMEZONE_OPTIONS } from "~/ui-rtk/utils/timezone-options"

const QA_JOB_TYPE = "meta-ads-qa"
const DEFAULT_TIME = "23:59"

/** "59 23 * * *" -> "23:59"; anything non-daily falls back to the default. */
function cronToTime(cron: string): string {
  const match = cron.match(/^(\d{1,2}) (\d{1,2}) \* \* \*$/)
  if (!match) return DEFAULT_TIME
  const [, minute, hour] = match
  return `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`
}

type QaScheduleInput = { slackUserIds?: string[]; slackChannelId?: string }

const SLACK_USER_ID_RE = /^[UW][A-Za-z0-9]{2,31}$/
const MAX_SLACK_USERS = 20

/** "U01, <@U02> U03" -> ["U01", "U02", "U03"] (deduped, upper-cased). */
function parseSlackUserIds(raw: string): string[] {
  const ids = raw
    .split(/[\s,]+/)
    .map(part =>
      part
        .replace(/^<@|>$/g, "")
        .trim()
        .toUpperCase(),
    )
    .filter(Boolean)
  return [...new Set(ids)]
}

/** "23:59" -> "59 23 * * *" (daily). */
function timeToCron(time: string): string {
  const [hour, minute] = time.split(":")
  return `${Number(minute)} ${Number(hour)} * * *`
}

/**
 * Per-company Ads QA setup, embedded in Settings → Ad Blaster (moved out of
 * the AdBlaster page's modal). Creates/updates a recurring `meta-ads-qa`
 * JobSchedule for the CURRENT company (daily, at the chosen time, in the
 * browser's timezone). Every company's findings digest goes to the one shared
 * ads QA Slack channel; the Slack users assigned here are @-mentioned on it.
 *
 * The users live on the schedule's `input` payload. Saves take the atomic
 * PATCH path (cron/timezone/enabled) whenever the input is unchanged; only a
 * users change (or a legacy per-company channel still on the input) forces
 * the non-atomic delete + create, whose failure mode is surfaced explicitly
 * ("schedule removed, recreation failed").
 */
export default function AdsQaScheduleSection() {
  const { data: schedules, isFetching: isListing } = useListJobSchedulesQuery({
    type: QA_JOB_TYPE,
  })
  const existing = useMemo(() => schedules?.[0], [schedules])
  const { data: fullSchedule } = useGetJobScheduleQuery(existing?.id ?? "", {
    skip: !existing,
  })

  const [createSchedule, { isLoading: isCreating }] =
    useCreateJobScheduleMutation()
  const [updateSchedule, { isLoading: isUpdating }] =
    useUpdateJobScheduleMutation()
  const [deleteSchedule, { isLoading: isDeleting }] =
    useDeleteJobScheduleMutation()
  const [runScheduleNow, { isLoading: isRunningNow }] =
    useRunJobScheduleNowMutation()
  const isSaving = isCreating || isUpdating || isDeleting

  // Fire the saved schedule immediately — same input (tagged Slack users
  // included) and enqueue path as the nightly cron tick. Runs whatever is
  // SAVED, not the unsaved form values.
  const handleRunNow = async () => {
    if (!existing) return
    try {
      await runScheduleNow(existing.id).unwrap()
      toast.success(
        "QA run started with this schedule's saved settings — findings land on the Jobs page and in Slack.",
      )
    } catch {
      toast.error("Could not start the test run. Please try again.")
    }
  }

  const [enabled, setEnabled] = useState(false)
  const [time, setTime] = useState(DEFAULT_TIME)
  const [timezone, setTimezone] = useState(BROWSER_TZ)
  const [slackUsers, setSlackUsers] = useState("")

  // Seed the form from the loaded schedule (and re-seed when the
  // input-bearing single-schedule fetch lands).
  useEffect(() => {
    setEnabled(existing?.enabled ?? false)
    setTime(existing ? cronToTime(existing.cron) : DEFAULT_TIME)
    setTimezone(existing?.timezone ?? BROWSER_TZ)
    const input = fullSchedule?.input as QaScheduleInput | undefined
    setSlackUsers((input?.slackUserIds ?? []).join(", "))
  }, [existing, fullSchedule])

  const handleSave = async () => {
    const userIds = parseSlackUserIds(slackUsers)
    const invalid = userIds.filter(id => !SLACK_USER_ID_RE.test(id))
    if (invalid.length > 0) {
      toast.error(
        `Slack users must be member IDs like U0123456789 (not @names or emails): ${invalid.join(", ")}`,
      )
      return
    }
    if (userIds.length > MAX_SLACK_USERS) {
      toast.error(`Tag at most ${MAX_SLACK_USERS} Slack users.`)
      return
    }

    const successToast = () =>
      toast.success(
        `Daily ads QA scheduled at ${time} (${timezone}) — findings go to the shared ads QA channel${
          userIds.length
            ? `, tagging ${userIds.length} user${userIds.length === 1 ? "" : "s"}`
            : ""
        }.`,
      )
    const create = () =>
      createSchedule({
        type: QA_JOB_TYPE,
        cron: timeToCron(time),
        timezone,
        input: userIds.length ? { slackUserIds: userIds } : {},
      }).unwrap()

    try {
      if (!enabled) {
        if (existing) {
          await updateSchedule({ id: existing.id, enabled: false }).unwrap()
          toast.success("Daily ads QA disabled for this company.")
        }
        return
      }

      if (existing) {
        // Cron/timezone/enabled are PATCHable — take the atomic path whenever
        // the users (which live on the non-PATCHable `input`) are unchanged.
        // A legacy per-company channel left on the input is dropped by
        // rewriting it once.
        const loadedInput = fullSchedule?.input as QaScheduleInput | undefined
        const inputChanged =
          userIds.join(",") !== (loadedInput?.slackUserIds ?? []).join(",") ||
          !!loadedInput?.slackChannelId
        if (!inputChanged) {
          await updateSchedule({
            id: existing.id,
            enabled: true,
            cron: timeToCron(time),
            timezone,
          }).unwrap()
          successToast()
          return
        }

        // Users changed → input must be rewritten, and input isn't
        // PATCHable, so this is an unavoidable two-step delete + create. If
        // the create fails after the delete committed, say EXACTLY that —
        // a generic "try again" would hide that the schedule is now gone.
        await deleteSchedule(existing.id).unwrap()
        try {
          await create()
        } catch {
          toast.error(
            "The previous schedule was removed but the new one could not be created — save again to re-enable daily QA.",
          )
          return
        }
        successToast()
        return
      }

      await create()
      successToast()
    } catch {
      toast.error("Could not save the QA schedule. Please try again.")
    }
  }

  return (
    <section className="rounded-lg border border-system-blue-700 bg-system-blue-800 text-white">
      <div className="border-b border-system-blue-700 px-system-lg py-system-md">
        <h2 className="text-lg font-semibold">Ads QA schedule</h2>
        <p className="text-system-gray-300 text-xs">
          Daily read-only QA of this company&apos;s newly-created Meta ads. It
          reports issues and, for ads whose UTMs don&apos;t match the company
          pattern (Settings → Company → UTM parameters), suggests the expected
          URL parameters — applied only when you click Apply on the job run.
        </p>
      </div>

      <div className="flex flex-col gap-system-lg px-system-lg py-system-md">
        {isListing && !schedules ? (
          <p className="text-sm text-system-gray-300">Loading…</p>
        ) : (
          <>
            <label className="flex cursor-pointer items-center gap-system-sm">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={enabled}
                onChange={e => setEnabled(e.target.checked)}
              />
              <span className="text-sm">Run ads QA daily for this company</span>
            </label>

            <section className="flex flex-col gap-system-2xs">
              <label
                htmlFor="ads-qa-time"
                className="text-sm font-medium text-system-gray-200"
              >
                Run at
              </label>
              <p className="text-system-gray-300 text-xs">
                Once a day, in the selected timezone. Each run reviews the ads
                created since the previous run&apos;s window.
              </p>
              <div className="flex items-center gap-system-sm">
                <input
                  id="ads-qa-time"
                  type="time"
                  value={time}
                  disabled={!enabled}
                  onChange={e => setTime(e.target.value || DEFAULT_TIME)}
                  className="input input-bordered h-10 w-40 bg-system-blue-900 py-0 text-sm disabled:opacity-50"
                />
                <Select
                  id="ads-qa-timezone"
                  options={TIMEZONE_OPTIONS}
                  value={timezone}
                  disabled={!enabled}
                  onChange={e => setTimezone(e.target.value)}
                />
              </div>
            </section>

            <section className="flex flex-col gap-system-2xs">
              <label
                htmlFor="ads-qa-slack-users"
                className="text-sm font-medium text-system-gray-200"
              >
                Slack users to tag (optional)
              </label>
              <p className="text-system-gray-300 text-xs">
                Findings for every company go to the shared ads QA channel. The
                users listed here are @-mentioned when this company&apos;s run
                finds issues. Use Slack member IDs (profile → ⋮ → Copy member
                ID), separated by commas.
              </p>
              <Input
                id="ads-qa-slack-users"
                value={slackUsers}
                onChange={e => setSlackUsers(e.target.value)}
                placeholder="U0123456789, U0987654321"
              />
            </section>
          </>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-system-blue-700 px-system-lg py-system-md">
        {/* Test-run the SAVED schedule (input + tagged users) without waiting for
            the cron tick. Hidden until a schedule exists to fire. */}
        {existing ? (
          <button
            type="button"
            onClick={() => void handleRunNow()}
            disabled={isRunningNow || isSaving}
            className="rounded-md border border-system-blue-600 px-system-md py-system-2xs text-sm font-medium text-system-gray-200 hover:bg-system-blue-700 hover:text-white disabled:opacity-50"
            title="Fire this schedule immediately with its saved settings"
          >
            {isRunningNow ? "Starting…" : "Run now"}
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving || isListing}
          className="rounded-md bg-system-green-600 px-system-md py-system-2xs text-sm font-medium text-white hover:bg-system-green-500 disabled:opacity-50"
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
      </div>
    </section>
  )
}
